import { useState, useEffect } from 'react';
import { Save, Download, Trash2, Eye, EyeOff, HelpCircle, Lock, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import DeletedItemsCard from '../components/common/DeletedItemsCard';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';
import { useLivingType } from '../store/settingsStore';
import { useTutorialStore } from '../store/tutorialStore';
import { useSubscriptionStore } from '../store/subscriptionStore';
import { useLivingTerms } from '../hooks/useLivingTerms';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface ActivityLogEntry {
  id: string;
  entityType: string;
  action: string;
  user: { firstName?: string; lastName?: string } | null;
  createdAt: string;
}

type TabType = 'personal' | 'activity';

/** Nombre d'entrées d'historique chargées à chaque fois */
const ACTIVITY_PAGE_SIZE = 20;

export default function Profile() {
  const { t, i18n } = useTranslation('profile');
  const dateLocale = i18n.language === 'fr' ? 'fr-FR' : 'en-US';
  const { user, setUser, logout } = useAuthStore();
  const navigate = useNavigate();
  const { startTutorial, resetTutorial } = useTutorialStore();
  const isPremiumOrAbove = useSubscriptionStore((s) => s.isPremiumOrAbove);

  // Termes adaptés au type de vie
  const livingType = useLivingType();
  const terms = useLivingTerms(livingType);

  // L'onglet actif est piloté par l'URL (?tab=...) : la navigation se fait
  // depuis le sous-menu « Profil » du menu principal, pas depuis la page.
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabType = tabParam === 'activity' && user?.coupleId ? 'activity' : 'personal';

  // Le tutoriel amène lui-même sur le bon onglet : ses étapes portent l'URL
  // complète (`/dashboard/profil?tab=…`), la page n'a rien à synchroniser.

  // Profil personnel
  const [personalProfile, setPersonalProfile] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
  });

  // Synchroniser l'état local quand le store user est mis à jour (ex: fetch DashboardLayout)
  useEffect(() => {
    setPersonalProfile({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
    });
  }, [user?.firstName, user?.lastName]);

  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Historique d'activité (chargé par pages successives)
  const [activityLog, setActivityLog] = useState<ActivityLogEntry[]>([]);
  const [activityTotal, setActivityTotal] = useState(0);
  const [isLoadingActivity, setIsLoadingActivity] = useState(false);
  const [isLoadingMoreActivity, setIsLoadingMoreActivity] = useState(false);

  const [showPasswords, setShowPasswords] = useState(false);
  const [isSavingPersonal, setIsSavingPersonal] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Charger l'historique d'activité quand l'onglet est sélectionné
  useEffect(() => {
    if (activeTab === 'activity' && user?.coupleId) {
      fetchActivityLog();
    }
  }, [activeTab, user?.coupleId]);

  // offset = 0 : premier chargement ; sinon on ajoute la page suivante à la liste
  const fetchActivityLog = async (offset = 0) => {
    const isFirstPage = offset === 0;
    if (isFirstPage) setIsLoadingActivity(true);
    else setIsLoadingMoreActivity(true);
    try {
      const response = await api.get(`/couple/activity?limit=${ACTIVITY_PAGE_SIZE}&offset=${offset}`);
      if (response.data.success) {
        const activities: ActivityLogEntry[] = response.data.data.activities || [];
        setActivityLog((prev) => (isFirstPage ? activities : [...prev, ...activities]));
        setActivityTotal(response.data.data.pagination?.total ?? activities.length);
      }
    } catch {
      // Silently fail
    } finally {
      if (isFirstPage) setIsLoadingActivity(false);
      else setIsLoadingMoreActivity(false);
    }
  };

  const handlePersonalProfileSave = async () => {
    setIsSavingPersonal(true);
    setMessage(null);
    try {
      const response = await api.put('/user/profile', personalProfile);
      if (response.data.success) {
        // Synchroniser aussi les champs couple dans le store
        const isPartner1 = user?.coupleRole === 'owner';
        const coupleSync = user?.coupleId
          ? isPartner1
            ? { partner1FirstName: personalProfile.firstName, partner1LastName: personalProfile.lastName }
            : { partner2FirstName: personalProfile.firstName, partner2LastName: personalProfile.lastName }
          : {};
        setUser({ ...user!, ...response.data.data, ...coupleSync });
        setMessage({ type: 'success', text: t('personalInfo.updated') });
      }
    } catch {
      setMessage({ type: 'error', text: t('updateError', { ns: 'common' }) });
    } finally {
      setIsSavingPersonal(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwords.newPassword !== passwords.confirmPassword) {
      setMessage({ type: 'error', text: t('password.mismatch') });
      return;
    }
    setIsSavingPassword(true);
    setMessage(null);
    try {
      await api.put('/user/password', {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });
      setMessage({ type: 'success', text: t('password.changed') });
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);
    } catch {
      setMessage({ type: 'error', text: t('password.incorrect') });
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await api.get('/user/export');
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `splinity-data-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setMessage({ type: 'error', text: t('data.exportError') });
    } finally {
      setIsExporting(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await api.delete('/user', { data: { password: deletePassword } });
      logout();
      navigate('/');
    } catch {
      setMessage({ type: 'error', text: t('data.deletePasswordError') });
    } finally {
      setIsDeleting(false);
    }
  };

  const getActionLabel = (action: string) => {
    const key = `activity.actions.${action}`;
    const translated = t(key);
    return translated !== key ? translated : action;
  };

  const getEntityLabel = (entityType: string) => {
    const key = `activity.entities.${entityType}`;
    const translated = t(key);
    return translated !== key ? translated : entityType;
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div data-tutorial="profil-title">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          {activeTab === 'personal' && t('tabs.personal')}
          {activeTab === 'activity' && t('tabs.activity')}
        </h1>
        {activeTab === 'personal' && <p className="text-gray-600">{t('description')}</p>}
      </div>

      {message && (
        <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'} border`}>
          {message.text}
        </div>
      )}

      {/* Contenu de la section sélectionnée dans le menu */}
      {activeTab === 'personal' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          {/* Colonne 1 : identité et sécurité */}
          <div className="space-y-6">
            {/* Informations personnelles */}
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('personalInfo.title')}</h2>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label={t('personalInfo.firstName')}
                    value={personalProfile.firstName}
                    onChange={(e) => setPersonalProfile({ ...personalProfile, firstName: e.target.value })}
                  />
                  <Input
                    label={t('personalInfo.lastName')}
                    value={personalProfile.lastName}
                    onChange={(e) => setPersonalProfile({ ...personalProfile, lastName: e.target.value })}
                  />
                </div>
                <div className="pt-2">
                  <p className="text-sm text-gray-500 mb-4">{t('personalInfo.email')} : {user?.email}</p>
                  <Button
                    variant="primary"
                    leftIcon={<Save className="w-4 h-4" />}
                    onClick={handlePersonalProfileSave}
                    isLoading={isSavingPersonal}
                  >
                    {t('personalInfo.save')}
                  </Button>
                </div>
              </div>
            </div>

            {/* Changement de mot de passe */}
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('password.title')}</h2>
              <div className="space-y-4">
                <div className="relative">
                  <Input
                    label={t('password.current')}
                    type={showPasswords ? 'text' : 'password'}
                    value={passwords.currentPassword}
                    onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="absolute right-3 top-8 text-gray-400"
                  >
                    {showPasswords ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                  <Input
                    label={t('password.new')}
                    type={showPasswords ? 'text' : 'password'}
                    value={passwords.newPassword}
                    onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                    helperText={t('validation.passwordRequirements', { ns: 'common' })}
                  />
                  <Input
                    label={t('password.confirm')}
                    type={showPasswords ? 'text' : 'password'}
                    value={passwords.confirmPassword}
                    onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                  />
                </div>
                <Button variant="primary" onClick={handlePasswordChange} isLoading={isSavingPassword}>
                  {t('password.change')}
                </Button>
              </div>
            </div>
          </div>

          {/* Colonne 2 : outils et données */}
          <div className="space-y-6">
            {/* Aide */}
            <div className="card" data-tutorial="restart-tutorial">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('help.title')}</h2>
              <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-primary-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{t('help.tutorialTitle')}</p>
                  <p className="text-sm text-gray-500">{t('help.tutorialDescription')}</p>
                </div>
                <Button
                  variant="secondary"
                  leftIcon={<HelpCircle className="w-4 h-4" />}
                  onClick={() => {
                    resetTutorial();
                    navigate('/dashboard');
                    setTimeout(() => startTutorial(), 500);
                  }}
                >
                  {t('help.restartTutorial')}
                </Button>
              </div>
            </div>

            {/* Éléments supprimés */}
            <DeletedItemsCard />

            {/* RGPD */}
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('data.title')}</h2>
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{t('data.exportTitle')}</p>
                    <p className="text-sm text-gray-500">{t('data.exportDescription')}</p>
                  </div>
                  {isPremiumOrAbove() ? (
                    <Button
                      variant="secondary"
                      leftIcon={<Download className="w-4 h-4" />}
                      onClick={handleExport}
                      isLoading={isExporting}
                    >
                      {t('data.export')}
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      leftIcon={<Lock className="w-4 h-4" />}
                      onClick={() => navigate('/dashboard/abonnement')}
                    >
                      {t('data.exportUpgrade')}
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-red-50 rounded-lg">
                  <div>
                    <p className="font-medium text-red-900">{t('data.deleteTitle')}</p>
                    <p className="text-sm text-red-700">{t('data.deleteDescription')}</p>
                  </div>
                  <Button
                    variant="danger"
                    leftIcon={<Trash2 className="w-4 h-4" />}
                    onClick={() => setShowDeleteModal(true)}
                  >
                    {t('data.delete')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'activity' && (
        <div className="space-y-6">
          <div className="card">
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
              <h2 className="text-lg font-semibold text-gray-900">{t('activity.title')}</h2>
              {activityTotal > 0 && (
                <span className="text-sm text-gray-500">
                  {t('activity.shownCount', { count: activityLog.length, total: activityTotal })}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mb-4">
              {t('activity.description', { group: terms.groupName })}
            </p>

            {isLoadingActivity ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : activityLog.length === 0 ? (
              <p className="text-center py-8 text-gray-500">{t('activity.noActivity')}</p>
            ) : (
              <div className="space-y-2">
                {activityLog.map((activity) => (
                  <div key={activity.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium text-gray-900 flex-1 min-w-0">
                      {getActionLabel(activity.action)} - {getEntityLabel(activity.entityType)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {t('activity.by')} {activity.user?.firstName || t('activity.user')} {activity.user?.lastName || ''}
                    </p>
                    <div className="text-sm text-gray-500 whitespace-nowrap sm:w-44 sm:text-right">
                      {new Date(activity.createdAt).toLocaleDateString(dateLocale, {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </div>
                  </div>
                ))}

                {/* Charger la page suivante */}
                {activityLog.length < activityTotal && (
                  <button
                    onClick={() => fetchActivityLog(activityLog.length)}
                    disabled={isLoadingMoreActivity}
                    className="w-full flex items-center justify-center gap-2 py-3 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {isLoadingMoreActivity ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                    {t('activity.loadMore', { count: Math.min(ACTIVITY_PAGE_SIZE, activityTotal - activityLog.length) })}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal suppression */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-red-600 mb-4">{t('data.deleteModalTitle')}</h2>
            <p className="text-gray-600 mb-4">
              {t('data.deleteModalMessage')}
            </p>
            <Input
              label={t('data.deleteConfirmPassword')}
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
            />
            <div className="flex gap-3 mt-6">
              <Button variant="secondary" className="flex-1" onClick={() => setShowDeleteModal(false)}>
                {t('cancel', { ns: 'common' })}
              </Button>
              <Button variant="danger" className="flex-1" onClick={handleDelete} isLoading={isDeleting}>
                {t('data.deleteConfirm')}
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
